import { writable, derived } from 'svelte/store';

const THEME_KEY = 'bau-da-gata-theme';

/**
 * Available themes
 */
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark'
};

/**
 * Get system theme preference
 * @returns {'light' | 'dark'}
 */
function getSystemTheme() {
  if (typeof window === 'undefined') return THEMES.LIGHT;
  // Default to light mode instead of following system preference
  return THEMES.LIGHT;
}

/**
 * Get initial theme from localStorage or system preference
 * @returns {'light' | 'dark'}
 */
function getInitialTheme() {
  if (typeof window === 'undefined') return THEMES.LIGHT;
  
  const stored = localStorage.getItem(THEME_KEY);
  if (stored && Object.values(THEMES).includes(stored)) {
    return stored;
  }
  
  return getSystemTheme();
}

/**
 * Theme store
 */
function createThemeStore() {
  const { subscribe, set, update } = writable(THEMES.LIGHT);

  return {
    subscribe,
    set: (value) => {
      if (typeof window !== 'undefined') {
        localStorage.setItem(THEME_KEY, value);
        applyTheme(value);
      }
      set(value);
    },
    toggle: () => {
      update(current => {
        const newTheme = current === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT;
        if (typeof window !== 'undefined') {
          localStorage.setItem(THEME_KEY, newTheme);
          applyTheme(newTheme);
        }
        return newTheme;
      });
    }
  };
}

/**
 * Apply theme to document
 * @param {'light' | 'dark'} themeName
 */
function applyTheme(themeName) {
  if (typeof document === 'undefined') return;
  
  document.documentElement.setAttribute('data-theme', themeName);
  
  // Update meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute(
      'content',
      themeName === THEMES.DARK ? '#1a1a2e' : '#ab26bd'
    );
  }
}

export const theme = createThemeStore();

/**
 * Derived store for checking if dark mode
 */
export const isDarkMode = derived(theme, ($theme) => $theme === THEMES.DARK);

/**
 * Toggle theme between light and dark
 */
export function toggleTheme() {
  theme.toggle();
}

/**
 * Set specific theme
 * @param {'light' | 'dark'} themeName
 */
export function setTheme(themeName) {
  if (Object.values(THEMES).includes(themeName)) {
    theme.set(themeName);
  }
}

/**
 * Initialize theme on app start
 */
export function initializeTheme() {
  const initialTheme = getInitialTheme();
  theme.set(initialTheme);
  
  // Listen for system theme changes
  if (typeof window !== 'undefined') {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', (e) => {
      // Only auto-switch if user hasn't set a preference
      const stored = localStorage.getItem(THEME_KEY);
      if (!stored) {
        theme.set(e.matches ? THEMES.DARK : THEMES.LIGHT);
      }
    });
  }
}
